import { useLocation, Link } from "react-router-dom";
import { useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Home, Phone, Calendar, ArrowLeft, Search } from "lucide-react";
import EnhancedSEO from "@/components/SEO/EnhancedSEO";

const NotFound = () => {
  const location = useLocation();

  useEffect(() => {
    console.error(
      "404 Error: User attempted to access non-existent route:",
      location.pathname
    );
  }, [location.pathname]);

  return (
    <>
      <EnhancedSEO 
        type="resources"
        title="Page Not Found | VGP Digital Marketing Agency"
        description="The page you're looking for doesn't exist. Explore our digital marketing services, pricing, and resources, or contact us for expert help with SEO and lead generation."
        keywords="404 error, page not found, digital marketing, SEO services, VGP"
        url="/404"
      />
      
      <div className="min-h-screen bg-gradient-to-br from-background via-background to-muted flex items-center justify-center px-4">
        <div className="text-center max-w-2xl mx-auto">
          {/* 404 Number with Gradient */}
          <div className="mb-8">
            <h1 className="text-8xl md:text-9xl font-bold bg-gradient-to-r from-primary via-secondary to-accent bg-clip-text text-transparent mb-4">
              404
            </h1>
            <div className="w-32 h-1 bg-gradient-to-r from-primary to-secondary mx-auto rounded-full"></div>
          </div>

          {/* Error Message */}
          <div className="mb-8">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
              Oops! Page Not Found
            </h2>
            <p className="text-lg text-muted-foreground mb-2">
              The page you're looking for seems to have wandered off into the digital wilderness.
            </p>
            <p className="text-muted-foreground">
              But don't worry – we've got plenty of ways to help you find what you need!
            </p>
          </div>

          {/* Action Buttons */}
          <div className="space-y-4 mb-12">
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button size="lg" className="bg-gradient-to-r from-primary to-secondary hover:from-primary/90 hover:to-secondary/90" asChild>
                <Link to="/">
                  <Home className="w-5 h-5 mr-2" />
                  Return Home
                </Link>
              </Button>
              <Button variant="outline" size="lg" asChild>
                <Link to="/pricing">
                  <Search className="w-5 h-5 mr-2" />
                  View Our Services
                </Link>
              </Button>
            </div>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button variant="premium" size="lg" asChild>
                <a href="/book-call">
                  <Calendar className="w-5 h-5 mr-2" />
                  Book Strategy Call
                </a>
              </Button>
              <Button variant="outline" size="lg" asChild>
                <a href="tel:07450620757">
                  <Phone className="w-5 h-5 mr-2" />
                  Call: 07450 620757
                </a>
              </Button>
            </div>
          </div>

          {/* Quick Navigation */}
          <div className="bg-card border border-border rounded-2xl p-6 shadow-lg">
            <h3 className="text-xl font-semibold mb-4 text-foreground">
              Popular Pages
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <Link 
                to="/pricing" 
                className="p-3 text-sm font-semibold text-accent hover:text-accent/80 hover:bg-accent/10 rounded-lg transition-colors text-center"
              >
                Pricing
              </Link>
              <Link 
                to="/seo-local-search" 
                className="p-3 text-sm font-semibold text-accent hover:text-accent/80 hover:bg-accent/10 rounded-lg transition-colors text-center"
              >
                Local SEO
              </Link>
              <Link 
                to="/seo-case-study" 
                className="p-3 text-sm font-semibold text-accent hover:text-accent/80 hover:bg-accent/10 rounded-lg transition-colors text-center"
              >
                Case Studies
              </Link>
              <Link 
                to="/faq" 
                className="p-3 text-sm font-semibold text-accent hover:text-accent/80 hover:bg-accent/10 rounded-lg transition-colors text-center"
              >
                FAQ
              </Link>
            </div>
          </div>

          {/* Bottom Message */}
          <div className="mt-8 text-sm text-muted-foreground">
            <p>Lost? Our team is here to help you navigate to digital growth success.</p>
          </div>
        </div>
      </div>
    </>
  );
};

export default NotFound;
